import { useMemo, useState } from "react";
import { format, isSameDay, isToday as isTodayFn } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "@/api/calendar";
import { EventPill } from "./EventPill";

interface DayCellProps {
  date: Date;
  events: CalendarEvent[];
  /** Whether this date sits inside the visible month (greyed out
   *  when not). */
  inCurrentMonth: boolean;
  /** Click on the empty area of the cell — opens the create-task
   *  flow with this date pre-filled. */
  onCreateAt: (date: Date) => void;
  /** Click on an event pill — opens the detail sheet. */
  onOpenEvent: (event: CalendarEvent) => void;
  /** Catalog predicate from useTaskStatuses. */
  isDone: (key: string | null | undefined) => boolean;
}

const MAX_VISIBLE = 3;

export function DayCell({
  date,
  events,
  inCurrentMonth,
  onCreateAt,
  onOpenEvent,
  isDone,
}: DayCellProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const today = isTodayFn(date);

  // Keep events deterministic: overdue first (always-rose), then by
  // due time, then by priority rank (already sorted server-side).
  const dayEvents = useMemo(
    () => events.filter((e) => isSameDay(new Date(e.at), date)),
    [events, date],
  );
  const visible = dayEvents.slice(0, MAX_VISIBLE);
  const overflow = dayEvents.length - visible.length;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        // Only fire create when the user clicks the empty body — not
        // an event pill (which has its own onClick) or the overflow
        // popover trigger.
        if ((e.target as HTMLElement).closest("[data-event-pill]")) return;
        if ((e.target as HTMLElement).closest("[data-overflow]")) return;
        onCreateAt(date);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCreateAt(date);
        }
      }}
      className={cn(
        "group relative flex min-h-[110px] flex-col gap-1 border-b border-r border-slate-200 px-2 py-1.5 text-left transition-colors",
        inCurrentMonth ? "bg-white" : "bg-slate-50/50",
        today && "bg-indigo-50/30",
        "hover:bg-indigo-500/[0.04] focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-indigo-300",
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full text-[11px] tabular-nums",
            today
              ? "bg-indigo-600 px-1 font-semibold text-white"
              : inCurrentMonth
                ? "font-medium text-slate-700"
                : "text-slate-400",
          )}
        >
          {format(date, "d")}
        </span>
      </div>
      <div className="flex flex-col gap-0.5">
        {visible.map((event) => (
          <span key={event.id} data-event-pill>
            <EventPill
              event={event}
              isDone={isDone}
              onClick={onOpenEvent}
            />
          </span>
        ))}
        {overflow > 0 && (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-overflow
                onClick={(e) => e.stopPropagation()}
                className="self-start rounded px-1 py-0.5 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50"
              >
                +{overflow} more
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2">
              <div className="mb-2 px-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {format(date, "EEEE, MMMM d")}
              </div>
              <div className="flex flex-col gap-1">
                {dayEvents.map((event) => (
                  <EventPill
                    key={event.id}
                    event={event}
                    variant="card"
                    isDone={isDone}
                    onClick={(e) => {
                      setPopoverOpen(false);
                      onOpenEvent(e);
                    }}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
